import { z } from 'zod';
/** ISO 4217 currencies supported by the platform. TND only for now. */
export const CURRENCIES = ['TND'];
/** Minor-unit exponent per currency. TND has 3 decimals (1 dinar = 1000 millimes). */
export const CURRENCY_EXPONENT = { TND: 3 };
export function money(minor, currency) {
    return { minor, currency };
}
function assertSameCurrency(a, b) {
    // Currency is a single-member union today; compare as strings so the guard stays meaningful
    // when more currencies are added.
    const left = a.currency;
    const right = b.currency;
    if (left !== right) {
        throw new RangeError(`Currency mismatch: ${left} vs ${right}`);
    }
}
export function addMoney(a, b) {
    assertSameCurrency(a, b);
    return money(a.minor + b.minor, a.currency);
}
export function subtractMoney(a, b) {
    assertSameCurrency(a, b);
    return money(a.minor - b.minor, a.currency);
}
/** Multiplies by an integer quantity (line totals). */
export function multiplyMoney(a, quantity) {
    return money(a.minor * quantity, a.currency);
}
/**
 * Parses a decimal major-unit string ("12.5", "-0.125", "7") into minor units without ever
 * going through a float. Rejects more fraction digits than the currency allows.
 */
export function parseMajor(value, currency) {
    const exponent = CURRENCY_EXPONENT[currency];
    const match = /^(-)?([0-9]+)(?:\.([0-9]+))?$/.exec(value.trim());
    if (match === null) {
        throw new RangeError(`Invalid amount: ${value}`);
    }
    const [, sign, whole = '0', fraction = ''] = match;
    if (fraction.length > exponent) {
        throw new RangeError(`Too many decimals for ${currency}: ${value}`);
    }
    const minor = BigInt(whole + fraction.padEnd(exponent, '0'));
    return money(sign === '-' ? -minor : minor, currency);
}
/** Formats minor units as a plain decimal major-unit string ("12.500"). */
export function toMajorString(amount) {
    const exponent = CURRENCY_EXPONENT[amount.currency];
    const negative = amount.minor < 0n;
    const digits = (negative ? -amount.minor : amount.minor).toString().padStart(exponent + 1, '0');
    const whole = digits.slice(0, digits.length - exponent);
    const fraction = digits.slice(digits.length - exponent);
    return `${negative ? '-' : ''}${whole}.${fraction}`;
}
/**
 * Locale-aware display. `Intl.NumberFormat` accepts decimal strings exactly (no float
 * conversion), so large amounts keep every digit.
 */
export function formatMoney(amount, locale) {
    const exponent = CURRENCY_EXPONENT[amount.currency];
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: amount.currency,
        minimumFractionDigits: exponent,
        maximumFractionDigits: exponent,
    }).format(toMajorString(amount));
}
/**
 * Wire format for money in JSON: minor units as a decimal string, because JSON numbers are
 * parsed as IEEE-754 doubles by most clients.
 */
export const moneySchema = z.object({
    minor: z
        .string()
        .regex(/^-?[0-9]{1,19}$/)
        .transform((value) => BigInt(value)),
    currency: z.enum(CURRENCIES),
});
export function moneyToJson(amount) {
    return { minor: amount.minor.toString(), currency: amount.currency };
}
//# sourceMappingURL=money.js.map